// askSmart-ui/src/pages/MapleCoin/operations/AccountsTab.tsx
//
// The institutional account structure: control accounts with their roll-ups,
// and the customer subaccounts that make up each control total. Customer
// subaccounts are identified by number only — this is the institution's
// position, not customer servicing. Read-only.
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { LedgerAccount, SubaccountPage, Subaccount, getAccounts, getSubaccounts } from './opsApi';
import { Amount, Busy, Loading, Panel, Status, Unavailable, titleCase } from './shared';
import { OpsNav } from './MapleCoinOperations';

function SubaccountsView({ control, onBack, nav }: { control: LedgerAccount; onBack: () => void; nav: OpsNav }) {
  const [page, setPage] = useState<SubaccountPage | null>(null);
  const [rows, setRows] = useState<Subaccount[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [more, setMore] = useState(false);

  const load = useCallback(async (after?: string) => {
    if (after) setMore(true);
    try {
      const p = await getSubaccounts(control.code, after);
      setPage(p);
      setRows((prev) => (after ? [...prev, ...p.subaccounts] : p.subaccounts));
      setState('ready');
    } catch {
      setState('error');
    } finally {
      setMore(false);
    }
  }, [control.code]);
  useEffect(() => { load(); }, [load]);

  const shownSum = rows.reduce((a, r) => a + BigInt(r.balanceMinor), BigInt(0)).toString();
  const complete = page && !page.nextAfter;
  return (
    <div className="mco-stack">
      <button type="button" className="mco-back" onClick={onBack}><ArrowLeft size={14} /> All accounts</button>
      <Panel title={`${control.name} · ${control.code}`} label="Subaccounts">
        {state === 'loading' && <Loading />}
        {state === 'error' && <Unavailable onRetry={() => load()} />}
        {state === 'ready' && page && (
          <>
            <dl className="mco-kv mco-kv--inline">
              <div><dt>Control balance</dt><dd><Amount minor={page.childrenBalanceMinor} currency={page.currency} testId="control-balance" /></dd></div>
              <div><dt>Customer subaccounts</dt><dd className="mco-num">{page.childAccounts}</dd></div>
            </dl>
            <div className="mco-table-wrap">
              <table className="mco-table">
                <thead><tr>
                  <th>Customer</th><th>Account</th><th>Currency</th><th>Status</th>
                  <th className="mco-r">Available</th><th className="mco-r">Pending</th>
                  <th className="mco-r">In this control</th>
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={7} className="mco-empty">No customer subaccounts.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.ledgerCode}>
                      <td><code>{r.customerNumber}</code></td>
                      <td><button type="button" className="mco-link" onClick={() => nav.openTransactions({ accountNumber: r.accountNumber })}>{r.accountNumber}</button></td>
                      <td>{r.currency}</td>
                      <td><Status value={r.accountStatus} /></td>
                      <td className="mco-r"><Amount minor={r.availableMinor} currency={r.currency} /></td>
                      <td className="mco-r"><Amount minor={r.pendingMinor} currency={r.currency} /></td>
                      <td className="mco-r"><Amount minor={r.balanceMinor} currency={r.currency} /></td>
                    </tr>
                  ))}
                </tbody>
                {complete && (
                  <tfoot><tr>
                    <td colSpan={6}>Sum of subaccounts</td>
                    <td className="mco-r"><Amount minor={shownSum} currency={page.currency} testId="subaccount-sum" /></td>
                  </tr></tfoot>
                )}
              </table>
            </div>
            {complete && (
              <p className={`mco-check ${shownSum === page.childrenBalanceMinor ? 'mco-check--ok' : 'mco-check--bad'}`}>
                {shownSum === page.childrenBalanceMinor ? 'Subaccounts sum exactly to the control balance.' : 'Subaccounts do not sum to the control balance.'}
              </p>
            )}
            {page.nextAfter && (
              <button type="button" className="mco-btn" disabled={more} onClick={() => load(page.nextAfter!)}>
                <Busy busy={more}>Load more</Busy>
              </button>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

export default function AccountsTab({ nav }: { nav: OpsNav }) {
  const [accounts, setAccounts] = useState<LedgerAccount[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [open, setOpen] = useState<LedgerAccount | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      setAccounts((await getAccounts()).accounts);
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (open) return <SubaccountsView control={open} onBack={() => setOpen(null)} nav={nav} />;
  if (state === 'loading') return <Loading label="Loading accounts" />;
  if (state === 'error' || !accounts) return <Unavailable onRetry={load} />;

  return (
    <Panel title="Institutional accounts" label="Institutional accounts">
      <div className="mco-table-wrap">
        <table className="mco-table">
          <thead><tr>
            <th>Code</th><th>Account</th><th>Type</th><th>Normal side</th><th className="mco-r">Balance</th><th>Structure</th><th />
          </tr></thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.code}>
                <td><code>{a.code}</code></td>
                <td>{a.name}{a.environmentScope === 'NON_PRODUCTION' && <span className="mco-tag">non-production</span>}</td>
                <td>{titleCase(a.accountType)}</td>
                <td>{titleCase(a.normalSide)}</td>
                <td className="mco-r"><Amount minor={a.balanceMinor} currency={a.currency} testId={`balance-${a.code}`} /></td>
                <td>{a.isControl ? `Control · ${a.childAccounts} customer subaccount${a.childAccounts === 1 ? '' : 's'}` : 'Postable'}</td>
                <td className="mco-r">
                  {a.isControl && <button type="button" className="mco-link" onClick={() => setOpen(a)}>View subaccounts</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mco-note">Control accounts take no entries of their own; each balance is the sum of its customer subaccounts.</p>
    </Panel>
  );
}
